import { useEffect, useState } from "react";
import { PageHeader } from "../components/layout/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { aiApi } from "../lib/api";
import { PROMPT_RULES } from "../lib/sample-data";
import { Cpu, KeyRound, ScrollText, Activity, Users, ShieldCheck, Workflow } from "lucide-react";
import { cn } from "../lib/utils";

const PROVIDERS = ["openai", "anthropic", "gemini"];
const PROVIDER_MODELS = {
  openai: ["gpt-5.2", "gpt-5.1", "gpt-5", "gpt-4.1", "o3"],
  anthropic: ["claude-sonnet-4-5-20250929", "claude-haiku-4-5-20251001", "claude-opus-4-5-20251101"],
  gemini: ["gemini-3-flash-preview", "gemini-3.1-pro-preview", "gemini-2.5-pro"],
};

const KEY_ROWS = [
  { provider: "OpenAI", env: "OPENAI_API_KEY", masked: "sk-····A6c9", scope: "live" },
  { provider: "Anthropic", env: "ANTHROPIC_API_KEY", masked: "sk-ant-····3F2", scope: "live" },
  { provider: "Gemini", env: "GEMINI_API_KEY", masked: "····P19", scope: "test" },
  { provider: "Nano Banana", env: "NANO_BANANA_API_KEY", masked: "—", scope: "—" },
  { provider: "ElevenLabs", env: "ELEVENLABS_API_KEY", masked: "el-····8a2", scope: "test" },
  { provider: "Twilio", env: "TWILIO_AUTH_TOKEN", masked: "tw-····77c", scope: "live" },
  { provider: "Stripe", env: "STRIPE_SECRET_KEY", masked: "sk_live_····tt9", scope: "live" },
];

const USAGE_LOGS = [
  { ts: "2026-02-05 14:21", lab: "BookLab", model: "claude-sonnet-4-5", tokens: 8420, cost: "$0.42", status: "ok" },
  { ts: "2026-02-05 14:19", lab: "SlideLab", model: "gpt-5.2", tokens: 6210, cost: "$0.31", status: "ok" },
  { ts: "2026-02-05 14:11", lab: "Copilot", model: "gpt-5.2", tokens: 1420, cost: "$0.07", status: "ok" },
  { ts: "2026-02-05 13:58", lab: "FlyerLab", model: "gpt-5.2", tokens: 980, cost: "$0.05", status: "ok" },
  { ts: "2026-02-05 13:42", lab: "WorkbookLab", model: "claude-sonnet-4-5", tokens: 12480, cost: "$0.62", status: "ok" },
  { ts: "2026-02-05 13:39", lab: "LaunchLab", model: "gpt-5.2", tokens: 2120, cost: "$0.10", status: "fail" },
];

export default function AdminLab() {
  const [routes, setRoutes] = useState([]);

  useEffect(() => {
    aiApi.routes().then(setRoutes).catch(() => setRoutes([]));
  }, []);

  return (
    <div data-testid="admin-lab-page" className="space-y-6">
      <PageHeader
        kicker="AdminLab"
        title="Platform intelligence & operations"
        description="Manage prompts, routing, API keys, integrations, and usage logs. Encrypted, masked, audited."
      />

      <Tabs defaultValue="dashboard" className="w-full">
        <TabsList
          data-testid="admin-tabs"
          className="grid grid-cols-3 md:grid-cols-7 gap-1 bg-[var(--pl-surface)]/60 border border-[var(--pl-border)] rounded-xl p-1 h-auto"
        >
          <Tab v="dashboard" icon={ShieldCheck}>Dashboard</Tab>
          <Tab v="prompts" icon={ScrollText}>Prompts</Tab>
          <Tab v="routing" icon={Workflow}>Routing</Tab>
          <Tab v="keys" icon={KeyRound}>API Keys</Tab>
          <Tab v="usage" icon={Activity}>Usage Logs</Tab>
          <Tab v="users" icon={Users}>Users</Tab>
          <Tab v="health" icon={Cpu}>Health</Tab>
        </TabsList>

        <TabsContent value="dashboard" className="mt-6 space-y-3" data-testid="admin-dashboard">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Active workspaces" value="48" />
            <Stat label="Projects (30d)" value="612" />
            <Stat label="AI calls (30d)" value="48,210" />
            <Stat label="Failed jobs" value="9" tone="bad" />
          </div>
          <Card>
            <Eyebrow>Today</Eyebrow>
            <ul className="mt-2 text-sm space-y-1.5 text-[var(--pl-text)]">
              <li>· 124 emails sent across 3 launches</li>
              <li>· 18 audiobook jobs completed (ElevenLabs)</li>
              <li>· 6 KDP packages exported</li>
              <li>· 1 webhook delivery failure (auto-retried)</li>
            </ul>
          </Card>
        </TabsContent>

        <TabsContent value="prompts" className="mt-6" data-testid="admin-prompts">
          <Card>
            <Eyebrow>Prompt Manager</Eyebrow>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)]">
                  <tr>
                    {["Lab", "Asset", "Model", "Tone", "Version"].map((h) => (
                      <th key={h} className="text-left py-2 font-normal">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="text-[var(--pl-text)]">
                  {PROMPT_RULES.map((r) => (
                    <tr key={r.id} data-testid={`prompt-${r.id}`} className="border-t border-[var(--pl-border)]">
                      <td className="py-2.5">{r.lab}</td>
                      <td>{r.asset}</td>
                      <td className="font-mono text-xs">{r.model}</td>
                      <td>{r.tone}</td>
                      <td className="font-mono text-[var(--pl-aqua)] text-xs">{r.version}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="routing" className="mt-6" data-testid="admin-routing">
          <Card>
            <Eyebrow>Model Routing</Eyebrow>
            <div className="mt-3 space-y-2">
              {(routes.length ? routes : []).map((r) => (
                <div
                  key={r.lab}
                  data-testid={`route-${r.lab}`}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-[var(--pl-border)] bg-white/[0.03]"
                >
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--pl-aqua)] w-24">
                    {r.lab}
                  </div>
                  <select
                    defaultValue={r.provider}
                    className="bg-[var(--pl-surface)] border border-[var(--pl-border)] rounded-lg h-9 px-2 text-xs text-[var(--pl-text)]"
                  >
                    {PROVIDERS.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                  <select
                    defaultValue={r.model}
                    className="bg-[var(--pl-surface)] border border-[var(--pl-border)] rounded-lg h-9 px-2 text-xs text-[var(--pl-text)] flex-1"
                  >
                    {(PROVIDER_MODELS[r.provider] || []).map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                  <select
                    defaultValue={r.quality || "balanced"}
                    className="bg-[var(--pl-surface)] border border-[var(--pl-border)] rounded-lg h-9 px-2 text-xs text-[var(--pl-text)]"
                  >
                    {["fast", "balanced", "premium", "custom"].map((q) => (
                      <option key={q} value={q}>{q}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="keys" className="mt-6" data-testid="admin-keys">
          <Card>
            <Eyebrow>API Key Manager</Eyebrow>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)]">
                  <tr>
                    {["Provider", "Env Var", "Key (masked)", "Scope", "Action"].map((h) => (
                      <th key={h} className="text-left py-2 font-normal">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="text-[var(--pl-text)]">
                  {KEY_ROWS.map((r) => (
                    <tr key={r.provider} data-testid={`key-${r.provider}`} className="border-t border-[var(--pl-border)]">
                      <td className="py-2.5">{r.provider}</td>
                      <td className="font-mono text-xs">{r.env}</td>
                      <td className="font-mono text-xs text-[var(--pl-aqua)]">{r.masked}</td>
                      <td>
                        <span
                          className={cn(
                            "inline-block text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-md border",
                            r.scope === "live"
                              ? "border-emerald-500/40 text-emerald-300"
                              : r.scope === "test"
                              ? "border-[var(--pl-aqua)]/40 text-[var(--pl-aqua)]"
                              : "border-[var(--pl-border)] text-[var(--pl-text-muted)]"
                          )}
                        >
                          {r.scope}
                        </span>
                      </td>
                      <td>
                        <button className="text-xs text-[var(--pl-aqua)] hover:underline">Rotate</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="usage" className="mt-6" data-testid="admin-usage">
          <Card>
            <Eyebrow>Usage Logs</Eyebrow>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)]">
                  <tr>
                    {["Time", "Lab", "Model", "Tokens", "Cost", "Status"].map((h) => (
                      <th key={h} className="text-left py-2 font-normal">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="text-[var(--pl-text)]">
                  {USAGE_LOGS.map((r, i) => (
                    <tr key={i} className="border-t border-[var(--pl-border)]">
                      <td className="py-2.5 font-mono text-xs">{r.ts}</td>
                      <td>{r.lab}</td>
                      <td className="font-mono text-xs">{r.model}</td>
                      <td>{r.tokens.toLocaleString()}</td>
                      <td className="font-mono text-[var(--pl-aqua)]">{r.cost}</td>
                      <td>
                        <span
                          className={cn(
                            "text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-md border",
                            r.status === "ok"
                              ? "border-emerald-500/40 text-emerald-300"
                              : "border-rose-500/40 text-rose-300"
                          )}
                        >
                          {r.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="mt-6" data-testid="admin-users">
          <Card>
            <Eyebrow>Users · Demo</Eyebrow>
            <p className="text-sm text-[var(--pl-text-muted)] mt-2">
              Single-user demo workspace for this MVP. Auth, RBAC, and tenant isolation are scheduled for the next iteration.
            </p>
          </Card>
        </TabsContent>

        <TabsContent value="health" className="mt-6" data-testid="admin-health">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="API uptime" value="99.98%" />
            <Stat label="DB latency" value="42ms" />
            <Stat label="LLM avg latency" value="2.1s" />
            <Stat label="Queue depth" value="3" />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Tab({ v, icon: I, children }) {
  return (
    <TabsTrigger
      value={v}
      data-testid={`admin-tab-${v}`}
      className="data-[state=active]:bg-[var(--pl-aqua)]/15 data-[state=active]:text-[var(--pl-aqua)] data-[state=active]:shadow-none rounded-lg text-xs justify-start gap-1.5"
    >
      <I className="h-3.5 w-3.5" />
      <span className="truncate">{children}</span>
    </TabsTrigger>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-2xl p-4 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-text-muted)]">{label}</div>
      <div className={cn("font-display text-2xl font-semibold mt-1", tone === "bad" ? "text-rose-300" : "text-[var(--pl-text)]")}>
        {value}
      </div>
    </div>
  );
}

function Card({ children }) {
  return <div className="rounded-2xl p-5 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60">{children}</div>;
}

function Eyebrow({ children }) {
  return <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">{children}</div>;
}
